"use client";

import { useCallback, useSyncExternalStore } from "react";
import {
  chordSymbolLabel,
  DEFAULT_CHORD_NOTATION,
  DEFAULT_NOTE_NOTATION,
  noteLabel,
  noteLabels,
  otherNotation,
  type NoteNotation,
} from "@/lib/music/notation";
import { DEFAULT_NOTE_NAMES_ON_SCORE, getNotationStore } from "@/lib/music/notation-store";

// Module-level so `useSyncExternalStore` sees stable references and doesn't
// re-subscribe on every render.
const subscribeNote = (cb: () => void) =>
  getNotationStore().noteNotation.subscribe(cb);
const subscribeChord = (cb: () => void) =>
  getNotationStore().chordNotation.subscribe(cb);
const subscribeChordPreference = (cb: () => void) =>
  getNotationStore().chordNotationPreference.subscribe(cb);

const readNote = () => getNotationStore().noteNotation.value;
const readChord = () => getNotationStore().chordNotation.value;
const readChordPreference = () =>
  getNotationStore().chordNotationPreference.value;
const subscribeNoteNames = (cb: () => void) =>
  getNotationStore().noteNamesOnScore.subscribe(cb);
const readNoteNames = () => getNotationStore().noteNamesOnScore.value;
const readNoteNamesOnServer = () => DEFAULT_NOTE_NAMES_ON_SCORE;

// The server snapshot deliberately ignores the persisted value: the prerendered
// HTML can only ever contain the defaults, so hydration must agree with them.
// React re-renders with the real (localStorage) value right after hydrating.
const readNoteOnServer = () => DEFAULT_NOTE_NOTATION;
const readChordOnServer = () => DEFAULT_CHORD_NOTATION;

/** Notation to use for bare note names. */
export function useNoteNotation(): NoteNotation {
  return useSyncExternalStore(subscribeNote, readNote, readNoteOnServer);
}

/** Notation to use for chord symbols, after the coherence rule. */
export function useChordNotation(): NoteNotation {
  return useSyncExternalStore(subscribeChord, readChord, readChordOnServer);
}

/** Faut-il écrire le nom des touches sur la partition ? */
export function useNoteNamesOnScore(): boolean {
  return useSyncExternalStore(subscribeNoteNames, readNoteNames, readNoteNamesOnServer);
}

/** Spells a single note name in the user's notation. */
export function useNoteLabel(): (name: string) => string {
  const notation = useNoteNotation();

  return useCallback((name: string) => noteLabel(name, notation), [notation]);
}

/** Spells a list of note names in the user's notation. */
export function useNoteLabels(): (names: string[]) => string[] {
  const notation = useNoteNotation();

  return useCallback((names: string[]) => noteLabels(names, notation), [
    notation,
  ]);
}

/** Spells a chord symbol in the user's notation. */
export function useChordLabel(): (symbol: string) => string {
  const notation = useChordNotation();

  return useCallback(
    (symbol: string) => chordSymbolLabel(symbol, notation),
    [notation],
  );
}

/**
 * Spells a note in the notation the user did NOT pick. Only for the surfaces
 * that show both spellings on purpose (the scale frieze).
 */
export function useOtherNoteLabel(): (name: string) => string {
  const notation = useNoteNotation();

  return useCallback(
    (name: string) => noteLabel(name, otherNotation(notation)),
    [notation],
  );
}

export interface NotationSettings {
  noteNotation: NoteNotation;
  /** What the user picked for chord symbols, even when overridden below. */
  chordNotation: NoteNotation;
  /** True while english note names force english chord symbols. */
  chordLocked: boolean;
  /** Les noms de touches écrits sur la partition. */
  noteNamesOnScore: boolean;
  setNoteNotation: (next: NoteNotation) => void;
  setChordNotation: (next: NoteNotation) => void;
  setNoteNamesOnScore: (next: boolean) => void;
}

/** Read/write access for the settings UI. */
export function useNotationSettings(): NotationSettings {
  const noteNotation = useNoteNotation();
  const chordNotation = useSyncExternalStore(
    subscribeChordPreference,
    readChordPreference,
    readChordOnServer,
  );

  const setNoteNotation = useCallback((next: NoteNotation) => {
    getNotationStore().setNoteNotation(next);
  }, []);

  const setChordNotation = useCallback((next: NoteNotation) => {
    getNotationStore().setChordNotation(next);
  }, []);

  const noteNamesOnScore = useNoteNamesOnScore();

  const setNoteNamesOnScore = useCallback((next: boolean) => {
    getNotationStore().setNoteNamesOnScore(next);
  }, []);

  return {
    noteNotation,
    chordNotation,
    chordLocked: noteNotation === "english",
    noteNamesOnScore,
    setNoteNotation,
    setChordNotation,
    setNoteNamesOnScore,
  };
}
